/**
 * ProductTypesAdmin.tsx — Admin > Product Types.
 *
 * The signed-in account's product types, functionally the corporate
 * dashboard's product types list scoped to one account (no account column or
 * filter, no compare, clone or push), laid out like every Admin list (Lead
 * Sources is the reference — see components/admin/AdminList.tsx):
 *   - columns: image, name (#id), kind (Product / Credit), disbursal
 *     (Appointment / Retail), active products, appointment types; Type Name,
 *     Kind and Disbursal sort from their titles (name order to start);
 *   - the corp toolbar: search by name (debounced 400ms, as corp), Category
 *     (product-type categories, by name) and Kind (Product / Credit)
 *     multi-select filters, and Show deleted;
 *   - "Add product type" top right; 25 rows a page with the pagination footer,
 *     back to page 1 whenever the search or a filter changes;
 *   - clicking a live row opens its edit form; Edit / Delete icons on a live
 *     row, Restore on a deleted one.
 *
 * Delete is refused while the type is still in use by active products or by
 * appointment types (as a required product or as their service credit). The
 * form is the shared ProductTypeFormModal, which Admin > Products also opens.
 * The product-type category list is read-only here; a type can only be
 * assigned existing categories.
 *
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a live row still opens its form, read-only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarCheck, FileText, ImageIcon, Package, Plus } from 'lucide-react';
import { CountPill, FlagIcon, OutlineBadge } from '@/components/admin/CorpBadges';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { ListFilterBar, anyFilterActive, type ListFilterConfig } from '@/components/admin/ListFilterBar';
import { useListPagination } from '@/components/admin/useListPagination';
import { useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { cn } from '@/lib/utils';
import {
  ProductTypeFormModal,
  isCreditProductType,
  isDeletedProductType,
} from '@/components/admin/ProductTypeForm';
import { useDebounce } from '@/hooks/useDebounce';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';
import * as inventorySvc from '@/services/inventory.service';
import type { ProductTypeLookupEntry } from '@/services/inventory.service';
import * as typesSvc from '@/services/productTypes.service';
import type { AttachedAppointmentType, ProductTypeDetail } from '@/services/productTypes.service';

/** The corp Kind filter: productCredits 0 = Product, 1 = Credit. */
const KIND_OPTIONS = [
  { value: '0', label: 'Product' },
  { value: '1', label: 'Credit' },
];

const SEARCH_DEBOUNCE_MS = 400;

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function typeName(t: ProductTypeLookupEntry): string {
  return (t.productTypeName ?? '').trim() || `Product type #${t.productTypeID}`;
}

/** Why a type cannot be deleted yet, or null when it can. */
function productTypeDeleteBlock(activeProducts: number, appointmentTypes: number): string | null {
  const reasons: string[] = [];
  if (activeProducts > 0) reasons.push(plural(activeProducts, 'active product', 'active products'));
  if (appointmentTypes > 0) reasons.push(plural(appointmentTypes, 'appointment type', 'appointment types'));
  if (reasons.length === 0) return null;
  return `This product type is attached to ${reasons.join(' and ')}. Remove them first.`;
}

type ModalState = { kind: 'new' } | { kind: 'edit'; id: number } | null;

/** The list's own order: by name, then id. */
function compareByName(a: ProductTypeLookupEntry, b: ProductTypeLookupEntry): number {
  return typeName(a).localeCompare(typeName(b)) || a.productTypeID - b.productTypeID;
}

/** Sort a column by the text it shows; ties keep the name order. */
const SORT_COMPARE: ListSortCompare<ProductTypeLookupEntry> = {
  name: compareByName,
  kind: (a, b) => Number(isCreditProductType(a)) - Number(isCreditProductType(b)), // Product before Credit
  disbursal: (a, b) => Number(a.disburseType ?? 0) - Number(b.disburseType ?? 0), // Appointment before Retail
};

// The corp Product Types columns (less Accounts), in corp order.
const COLUMNS: AdminListColumn[] = [
  { key: 'name', header: 'Type Name', sortable: true },
  {
    key: 'hasImage',
    header: <ImageIcon className="mx-auto h-3.5 w-3.5 text-slate-400" aria-label="Has Image" />,
    align: 'center',
    className: 'w-12 px-1',
  },
  {
    key: 'hasDescription',
    header: <FileText className="mx-auto h-3.5 w-3.5 text-slate-400" aria-label="Has Description" />,
    align: 'center',
    className: 'w-12 px-1',
  },
  { key: 'kind', header: 'Kind', className: 'w-28', sortable: true },
  { key: 'disbursal', header: 'Disbursal', className: 'w-28', sortable: true },
  { key: 'products', header: 'Products', className: 'w-28' },
  { key: 'appointmentTypes', header: 'Appt Types', className: 'w-28' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

export function ProductTypesAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [types, setTypes] = useState<ProductTypeLookupEntry[]>([]);
  const [images, setImages] = useState<Map<number, string>>(new Map());
  const [attachments, setAttachments] = useState<Map<number, AttachedAppointmentType[]>>(new Map());
  const [categoryNames, setCategoryNames] = useState<Map<number, string[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedKinds, setSelectedKinds] = useState<string[]>([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [modal, setModal] = useState<ModalState>(null);
  const [deleting, setDeleting] = useState<ProductTypeLookupEntry | null>(null);

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const [list, imageMap, apptMap, catMap] = await Promise.all([
        inventorySvc.listProductTypes(),
        typesSvc.listProductTypeImageMap().catch(() => new Map<number, string>()),
        typesSvc.listAppointmentTypeAttachments().catch(() => new Map<number, AttachedAppointmentType[]>()),
        typesSvc.listProductTypeCategoryNames().catch(() => new Map<number, string[]>()),
      ]);
      setTypes(Array.isArray(list) ? list : []);
      setImages(imageMap);
      setAttachments(apptMap);
      setCategoryNames(catMap);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load product types'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const apptCount = useCallback((id: number) => attachments.get(id)?.length ?? 0, [attachments]);

  // The corp Category filter lists every category name a type is filed under.
  const categoryOptions = useMemo(() => {
    const names = new Set<string>();
    categoryNames.forEach((list) => list.forEach((n) => names.add(n)));
    return [...names].sort((a, b) => a.localeCompare(b)).map((n) => ({ value: n, label: n }));
  }, [categoryNames]);

  const filters: ListFilterConfig[] = [
    {
      id: 'product-types-category-filter',
      label: 'Filter by category',
      placeholder: 'All categories',
      searchPlaceholder: 'Search categories…',
      emptyMessage: 'No categories found.',
      options: categoryOptions,
      selected: selectedCategories,
      onChange: setSelectedCategories,
    },
    {
      id: 'product-types-kind-filter',
      label: 'Filter by kind',
      placeholder: 'All kinds',
      options: KIND_OPTIONS,
      selected: selectedKinds,
      onChange: setSelectedKinds,
      widthClass: 'w-40',
    },
  ];
  const filtering = anyFilterActive(filters);

  const query = debouncedSearch.trim().toLowerCase();
  const rows = useMemo(() => {
    const kinds = new Set(selectedKinds);
    const cats = new Set(selectedCategories);
    return types
      .filter((t) => (showDeleted ? true : !isDeletedProductType(t)))
      .filter((t) => kinds.size === 0 || kinds.has(isCreditProductType(t) ? '1' : '0'))
      .filter((t) => cats.size === 0 || (categoryNames.get(t.productTypeID) ?? []).some((n) => cats.has(n)))
      .filter((t) => (query === '' ? true : typeName(t).toLowerCase().includes(query) || String(t.productTypeID) === query))
      .sort(compareByName);
  }, [types, showDeleted, selectedKinds, selectedCategories, categoryNames, query]);

  // Opens in the name order above; a column title re-sorts.
  const sorting = useListSort(rows, { initialKey: 'name', compare: SORT_COMPARE });

  const pagination = useListPagination(sorting.sorted, {
    resetOn: [query, showDeleted, selectedCategories, selectedKinds],
  });

  function handleSaved(saved: ProductTypeDetail, isNew: boolean, imageUrl: string | null) {
    const patch: Partial<ProductTypeLookupEntry> = {
      productTypeID: saved.productTypeID,
      productTypeName: saved.productTypeName,
      productCredits: saved.productCredits,
      disburseType: saved.disburseType,
      description: saved.description,
    };
    setTypes((prev) =>
      isNew
        ? [...prev, { ...patch, productTypeID: saved.productTypeID, productTypeName: saved.productTypeName, status: 1, activeProductsCount: 0 }]
        : prev.map((t) => (t.productTypeID === saved.productTypeID ? { ...t, ...patch } : t)),
    );
    if (imageUrl) handleImageChanged(saved.productTypeID, imageUrl);
    setModal(null);
    // The form may have changed the type's categories; the Category filter reads them.
    typesSvc
      .listProductTypeCategoryNames()
      .then(setCategoryNames)
      .catch(() => {});
  }

  function handleImageChanged(id: number, url: string) {
    setImages((prev) => new Map(prev).set(id, url));
  }

  function requestDelete(t: ProductTypeLookupEntry) {
    const block = productTypeDeleteBlock(Number(t.activeProductsCount ?? 0), apptCount(t.productTypeID));
    if (block) {
      toast.error(block);
      return;
    }
    setDeleting(t);
  }

  async function handleDelete() {
    if (!deleting) return;
    const id = deleting.productTypeID;
    try {
      await typesSvc.deleteProductType(id);
      setTypes((prev) => prev.map((t) => (t.productTypeID === id ? { ...t, status: 0 } : t)));
      toast.success('Product type deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The product type could not be deleted.'));
    }
  }

  async function handleRestore(t: ProductTypeLookupEntry) {
    try {
      await typesSvc.restoreProductType(t.productTypeID);
      setTypes((prev) => prev.map((x) => (x.productTypeID === t.productTypeID ? { ...x, status: 1 } : x)));
      toast.success('Product type restored');
    } catch (err) {
      // A 422 here is DUPLICATE_PRODUCT_TYPE_NAME: a live type already has the name.
      toast.error(getErrorMessage(err, 'The product type could not be restored.'));
    }
  }

  const narrowed = query !== '' || filtering;

  return (
    <AdminListPage
      title="Product Types"
      subtitle="What products are, how they are disbursed, and which plans and categories they belong to."
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModal({ kind: 'new' })}>
          Add product type
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name…"
          searchLabel="Search product types"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="product-types-show-deleted"
        >
          <ListFilterBar filters={filters} />
        </AdminListToolbar>
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading product types…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={narrowed ? 'No product types found' : 'No product types yet'}
          hint={narrowed ? 'Try adjusting your search or filters.' : locked ? undefined : 'Add a product type to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Product types" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((t) => {
              const id = t.productTypeID;
              const name = typeName(t);
              const deleted = isDeletedProductType(t);
              const url = images.get(id);
              return (
                <tr
                  key={id}
                  data-product-type-id={id}
                  className={adminRowClass({ deleted, clickable: !deleted })}
                  // A live row opens its form; a deleted one is restored, not edited.
                  onClick={deleted ? undefined : () => setModal({ kind: 'edit', id })}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded border border-slate-200 bg-slate-50">
                        {url ? (
                          <img src={url} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <Package className="h-4 w-4 text-slate-400" aria-hidden />
                        )}
                      </div>
                      <AdminRowName
                        deleted={deleted}
                        nameTestId="product-type-name"
                        suffix={<span className="font-mono text-[11px] text-slate-400">#{id}</span>}
                      >
                        {name}
                      </AdminRowName>
                    </div>
                  </td>
                  <td className={cn(ADMIN_TD_CLASS, 'px-1 text-center')}>
                    <FlagIcon on={Boolean(url)} icon={<ImageIcon />} className="text-green-500" label="Has image" />
                  </td>
                  <td className={cn(ADMIN_TD_CLASS, 'px-1 text-center')}>
                    <FlagIcon on={Boolean(t.description?.trim())} icon={<FileText />} className="text-purple-500" label="Has description" />
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    <OutlineBadge>{isCreditProductType(t) ? 'Credit' : 'Product'}</OutlineBadge>
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    {Number(t.disburseType ?? 0) === 1 && <OutlineBadge tone="emerald">Retail</OutlineBadge>}
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    <CountPill tone="blue" icon={<Package />} count={Number(t.activeProductsCount ?? 0)} title="Active products" />
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    <CountPill tone="orange" icon={<CalendarCheck />} count={apptCount(id)} title="Appointment types" />
                  </td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore(t)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setModal({ kind: 'edit', id })} />
                            <AdminRowAction
                              kind="delete"
                              label={`Delete ${name}`}
                              // In use (attached products or appointment types): greyed out, as on corp;
                              // a click still says why.
                              blockedReason={productTypeDeleteBlock(Number(t.activeProductsCount ?? 0), apptCount(id))}
                              onClick={() => requestDelete(t)}
                            />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['product type', 'product types']} />
        </>
      )}

      {modal && (
        <ProductTypeFormModal
          key={modal.kind === 'edit' ? `edit-${modal.id}` : 'new'}
          productTypeId={modal.kind === 'edit' ? modal.id : null}
          allTypes={types}
          showAttachments
          attachedAppointmentTypes={modal.kind === 'edit' ? attachments.get(modal.id) ?? [] : []}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
          onImageChanged={handleImageChanged}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete product type?"
        message={deleting ? `${typeName(deleting)} will be deleted. You can restore it from Show deleted.` : ''}
        confirmLabel="Delete product type"
        variant="danger"
      />
    </AdminListPage>
  );
}
